import React, { useMemo, useRef, useState, useCallback } from 'react';
import {
  Grid, GridHeaderCell, TimeHeaderCell, TimeColumn, TimeCell,
  GridColumn, HourLine, Block, Badge, EmptyState, DragGhost, ResizeHandle, ConflictMark,
  ColumnCampus, HGrid, HInner, HCorner, HTimeHeader, HTimeTick,
  HLaneLabel, HLaneCampus, HLaneTrack, HHourLine,
  TIME_START, TIME_END, HOUR_HEIGHT, HOUR_WIDTH, LANE_HEIGHT, MIN_BLOCK_PX
} from './Timetable.styles';
import {
  formatEnrolment, formatStudentNames, isCancelled, isNoShow, hasConflict, isBusy
} from '../../../api/timetableApi';

/**
 * Time-axis grid with one column per room or per teacher.
 *
 * Room View and Teacher View are the same grid — only the axis differs — so they share this
 * component rather than each keeping its own copy of the layout, the overlap maths and the
 * drag handling.
 *
 * Editing gestures (all pointer-based, so they work with mouse, pen and touch alike):
 *   - drag a block           -> move it: to another column, another time, or onto a day tab
 *   - drag a block's bottom  -> change its end time
 *   - click empty grid space -> add a lesson there
 * The grid only reports the intended change; deciding whether it applies to one week or the
 * whole term is the page's job.
 */

const UNASSIGNED = '__unassigned__';
const SNAP_MINUTES = 15;
const DRAG_THRESHOLD_PX = 4;
const MIN_DURATION_MIN = 15;

const toMinutes = (time) => {
  const [h, m] = (time || '0:0').split(':').map(Number);
  return h * 60 + m;
};

const toTime = (minutes) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

const snap = (minutes) => Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;

const clampToGrid = (startMin, durationMin, gridStartMin, gridEndMin) => {
  const start = Math.min(Math.max(startMin, gridStartMin), gridEndMin - durationMin);
  return { start, end: start + durationMin };
};

/**
 * The hours the grid has to draw for this week.
 *
 * 08:00 to 22:00 covers everything a campus normally runs, but it is a default rather than a
 * limit: a class recorded outside it — an early Sunday start, an evening session, or a time
 * someone mistyped — must still appear, or the timetable quietly hides the very row that
 * needs correcting. The window therefore stretches to contain whatever is actually booked and
 * never shrinks below the default, so the grid keeps its familiar shape on an ordinary week.
 */
const timeWindow = (occurrences) => {
  let startMin = TIME_START * 60;
  let endMin = TIME_END * 60;
  // A blank time parses to 0 and would drag the window back to midnight, so only real
  // "HH:MM" values count.
  const at = (value) => (/^\d{1,2}:\d{2}$/.test(value || '') ? toMinutes(value) : null);
  (occurrences || []).forEach(occ => {
    const from = at(occ.startTime);
    const to = at(occ.endTime);
    if (from !== null) startMin = Math.min(startMin, from);
    if (to !== null) endMin = Math.max(endMin, to);
  });
  return {
    startMin: Math.max(0, Math.floor(startMin / 60) * 60),
    endMin: Math.min(24 * 60, Math.ceil(endMin / 60) * 60)
  };
};

/**
 * The two orientations, as one geometry contract.
 *
 * The grid always draws lanes (rooms / teachers / days) along one axis and time along the
 * other; only which screen axis carries which changes. Routing every measurement through one
 * of these descriptors keeps the layout, the pointer maths and the drag preview written once,
 * instead of forking ~300 lines of gesture handling per direction.
 *
 * `time*` members map clock minutes to the axis time runs along; `lane*` members map a lane's
 * position and share of its cluster to the other axis.
 */
const makeAxes = (GRID_START_MIN, trackHours) => ({
  vertical: {
    // Time runs down the page, lanes are columns.
    blockStyle: (startMin, endMin) => ({
      $top: `${((startMin - GRID_START_MIN) / 60) * HOUR_HEIGHT}px`,
      $height: `${Math.max(((endMin - startMin) / 60) * HOUR_HEIGHT, MIN_BLOCK_PX)}px`
    }),
    laneStyle: (offsetPct, sizePct, zIndex) => ({
      $left: `calc(${offsetPct}% + 2px)`,
      $width: `calc(${sizePct}% - 4px)`,
      $zIndex: zIndex
    }),
    // How much room the card has for text: along time when time is vertical. A long class
    // that has its column to itself gets a fourth line, which is where the headcount goes
    // once the third line is carrying student names.
    textRows: (startMin, endMin, lanes) => {
      const px = ((endMin - startMin) / 60) * HOUR_HEIGHT;
      if (px > 104 && lanes < 2) return 4;
      if (px > 74 && lanes < 3) return 3;
      if (px > 52) return 2;
      return 1;
    },
    deltaMinutes: (dx, dy) => (dy / HOUR_HEIGHT) * 60,
    minutesAtPoint: (rect, event) =>
      GRID_START_MIN + ((event.clientY - rect.top) / HOUR_HEIGHT) * 60,
    laneHit: (rect, event) => event.clientX >= rect.left && event.clientX < rect.right
  },
  horizontal: {
    // Time runs left to right, lanes are rows.
    blockStyle: (startMin, endMin) => ({
      $left: `${((startMin - GRID_START_MIN) / 60) * HOUR_WIDTH}px`,
      $width: `${Math.max(((endMin - startMin) / 60) * HOUR_WIDTH, MIN_BLOCK_PX)}px`
    }),
    laneStyle: (offsetPct, sizePct, zIndex) => ({
      $top: `calc(${offsetPct}% + 2px)`,
      $height: `calc(${sizePct}% - 4px)`,
      $zIndex: zIndex
    }),
    // Here the card's height is the lane's share of the row, and its width is the duration.
    textRows: (startMin, endMin, lanes) => {
      const height = LANE_HEIGHT / lanes;
      const width = ((endMin - startMin) / 60) * HOUR_WIDTH;
      if (height < 30 || width < 90) return 1;
      return height > 52 ? 3 : 2;
    },
    deltaMinutes: (dx) => (dx / HOUR_WIDTH) * 60,
    minutesAtPoint: (rect, event) =>
      GRID_START_MIN + ((event.clientX - rect.left) / HOUR_WIDTH) * 60,
    laneHit: (rect, event) => event.clientY >= rect.top && event.clientY < rect.bottom
  },
  trackWidth: trackHours * HOUR_WIDTH
});

/**
 * Side-by-side placement for classes that overlap in the same column.
 *
 * Two classes booked into one room at the same time is exactly the clash an admin opens
 * this page to find, so they are shown next to each other rather than stacked and hidden.
 *
 * Overlap is decided on CLOCK MINUTES, never on rendered pixels. Pixel tops carry floating
 * point error — `(16 + 40/60 - 8) * 56` does not compare equal to itself computed the other
 * way — and `position()` also floors short classes to a minimum height, pushing their bottom
 * edge past their real end time. Both made back-to-back classes (one ends 16:40, the next
 * starts 16:40) look overlapping and split them into half-width columns. The rule here is the
 * same one the backend uses in `detect_conflicts`: touching end-to-start is not an overlap.
 */
const overlaps = (a, b) => a.startMin < b.endMin && b.startMin < a.endMin;

/**
 * Pack overlapping classes into as few side-by-side lanes as possible.
 *
 * Width has to be decided per *cluster*, not per class: if A overlaps B and B overlaps C but
 * A and C do not, all three must agree on the same lane count, otherwise A renders half-width
 * while B renders a third and the column looks broken. So group into connected clusters
 * first, then give every member of a cluster the same geometry.
 */
const layOutColumn = (items, axis) => {
  const placed = items.map(item => {
    const startMin = toMinutes(item.startTime);
    const endMin = toMinutes(item.endTime);
    return { ...item, startMin, endMin, ...axis.blockStyle(startMin, endMin) };
  });
  placed.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  // Connected clusters: walk in start order, keeping a cluster open while anything in it
  // still runs past the next class's start.
  const clusters = [];
  let current = [];
  let clusterEnd = -1;
  placed.forEach(item => {
    if (current.length > 0 && item.startMin >= clusterEnd) {
      clusters.push(current);
      current = [];
      clusterEnd = -1;
    }
    current.push(item);
    clusterEnd = Math.max(clusterEnd, item.endMin);
  });
  if (current.length > 0) clusters.push(current);

  const result = [];
  clusters.forEach(cluster => {
    if (cluster.length === 1) {
      const only = cluster[0];
      result.push({
        ...only,
        ...axis.laneStyle(0, 100, 1),
        rows: axis.textRows(only.startMin, only.endMin, 1)
      });
      return;
    }


    // Greedy lane assignment: reuse the first lane whose last class has already finished.
    const lanes = [];
    cluster.forEach(item => {
      let lane = lanes.findIndex(occupants => !overlaps(occupants[occupants.length - 1], item));
      if (lane === -1) {
        lanes.push([item]);
        lane = lanes.length - 1;
      } else {
        lanes[lane].push(item);
      }
      item.lane = lane;
    });

    const share = 100 / lanes.length;
    cluster.forEach(item => {
      result.push({
        ...item,
        ...axis.laneStyle(item.lane * share, share, item.lane + 1),
        rows: axis.textRows(item.startMin, item.endMin, lanes.length)
      });
    });
  });

  return result;
};

const buildColumns = (occurrences, groupBy, axis) => {
  const columns = axis.map(entry => ({
    key: groupBy === 'teacher' ? entry.id : entry.key,
    label: entry.name || '—',
    campusId: groupBy === 'room' ? entry.campusId : '',
    // Only rooms carry a campus, and only worth showing when the grid spans more than one:
    // two campuses can each have a "Room 3" and the header is the only thing telling them
    // apart. Teachers are people, so one column each regardless of where they teach.
    campus: groupBy === 'room' ? entry.campusName : '',
    sublabel: `${entry.count} ${entry.count === 1 ? 'class' : 'classes'}`,
    items: []
  }));
  const byKey = new Map(columns.map(c => [c.key, c]));
  const unassigned = { key: UNASSIGNED, label: 'Unassigned', sublabel: '', items: [] };

  occurrences.forEach(occurrence => {
    if (groupBy === 'teacher') {
      const teachers = occurrence.teachers || [];
      if (teachers.length === 0) {
        unassigned.items.push(occurrence);
        return;
      }
      // A class with two teachers genuinely occupies both of their columns
      teachers.forEach(teacher => {
        const column = byKey.get(teacher.id);
        (column ? column.items : unassigned.items).push(occurrence);
      });
      return;
    }
    if (groupBy === 'day') {
      const column = byKey.get(occurrence.date);
      if (column) column.items.push(occurrence);
      return;
    }
    const column = byKey.get(occurrence.roomKey);
    (column ? column.items : unassigned.items).push(occurrence);
  });

  // A day column is a fixed set — an empty Tuesday still has to be shown — so there is no
  // "unassigned" bucket to fall into.
  if (groupBy !== 'day' && unassigned.items.length > 0) {
    unassigned.sublabel = `${unassigned.items.length} classes`;
    columns.push(unassigned);
  }
  if (groupBy === 'day') {
    columns.forEach(column => {
      column.sublabel = column.items.length === 1 ? '1 class' : `${column.items.length} classes`;
    });
  }
  return columns;
};

const GridView = ({
  occurrences, groupBy, axis, colorOf, onSelect, emptyHint,
  orientation = 'vertical',
  canEdit = false, multiCampus = false, onMove, onResize, onCreate, onDayHover
}) => {
  const horizontal = orientation === 'horizontal';
  const { startMin: gridStartMin, endMin: gridEndMin } = useMemo(
    () => timeWindow(occurrences), [occurrences]
  );
  const trackHours = (gridEndMin - gridStartMin) / 60;
  const axes = useMemo(
    () => makeAxes(gridStartMin, trackHours), [gridStartMin, trackHours]
  );
  const geom = axes[orientation] || axes.vertical;

  const columns = useMemo(() => buildColumns(occurrences, groupBy, axis), [occurrences, groupBy, axis]);
  const laidOut = useMemo(
    () => columns.map(column => ({ ...column, items: layOutColumn(column.items, geom) })),
    [columns, geom]
  );
  const hours = useMemo(() => {
    const result = [];
    for (let m = gridStartMin; m < gridEndMin; m += 60) {
      result.push(`${String(m / 60).padStart(2, '0')}:00`);
    }
    return result;
  }, [gridStartMin, gridEndMin]);

  const columnRefs = useRef(new Map());
  const setColumnRef = (key) => (el) => {
    if (el) columnRefs.current.set(key, el);
    else columnRefs.current.delete(key);
  };

  // One gesture at a time. Kept in a ref for the pointer handlers and mirrored into state
  // only for what needs to render (the ghost and the highlighted drop column).
  const gesture = useRef(null);
  const [drag, setDrag] = useState(null);

  const laneAtPoint = useCallback((event) => {
    for (const [key, el] of columnRefs.current.entries()) {
      const rect = el.getBoundingClientRect();
      if (geom.laneHit(rect, event)) return { key, rect };
    }
    return null;
  }, [geom]);

  const dayTabAtPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const tab = el && el.closest('[data-day-tab]');
    return tab ? Number(tab.getAttribute('data-day-tab')) : null;
  };

  const beginGesture = (event, item, columnKey, mode) => {
    if (!canEdit || event.button !== 0) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    gesture.current = {
      mode, item, columnKey,
      startX: event.clientX, startY: event.clientY,
      startMin: toMinutes(item.startTime), endMin: toMinutes(item.endTime),
      moved: false
    };
  };

  const handlePointerMove = (event) => {
    const g = gesture.current;
    if (!g) return;
    const dx = event.clientX - g.startX;
    const dy = event.clientY - g.startY;
    if (!g.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    g.moved = true;

    const deltaMin = snap(geom.deltaMinutes(dx, dy));

    if (g.mode === 'resize') {
      const endMin = Math.max(g.startMin + MIN_DURATION_MIN, Math.min(g.endMin + deltaMin, gridEndMin));
      g.preview = { columnKey: g.columnKey, startMin: g.startMin, endMin };
    } else {
      const duration = g.endMin - g.startMin;
      const { start, end } = clampToGrid(g.startMin + deltaMin, duration, gridStartMin, gridEndMin);
      const over = laneAtPoint(event);
      const dayIndex = dayTabAtPoint(event.clientX, event.clientY);
      g.preview = { columnKey: over ? over.key : g.columnKey, startMin: start, endMin: end, dayIndex };
      if (onDayHover) onDayHover(dayIndex);
    }

    setDrag({ ...g.preview, item: g.item, mode: g.mode });
  };

  const handlePointerUp = (event) => {
    const g = gesture.current;
    gesture.current = null;
    setDrag(null);
    if (onDayHover) onDayHover(null);
    if (!g) return;

    if (!g.moved) {
      onSelect(g.item);
      return;
    }

    const p = g.preview;
    if (!p) return;

    if (g.mode === 'resize') {
      const endTime = toTime(p.endMin);
      if (endTime !== g.item.endTime && onResize) onResize(g.item, endTime);
      return;
    }

    const targetColumn = columns.find(c => c.key === p.columnKey);
    const change = {
      startTime: toTime(p.startMin),
      endTime: toTime(p.endMin),
      dayIndex: p.dayIndex
    };
    if (p.columnKey !== g.columnKey && targetColumn && targetColumn.key !== UNASSIGNED) {
      change.columnKey = targetColumn.key;
      change.columnName = targetColumn.label;
    }

    const unchanged = change.startTime === g.item.startTime
      && change.endTime === g.item.endTime
      && change.columnKey === undefined
      && (change.dayIndex === null || change.dayIndex === undefined);
    if (!unchanged && onMove) onMove(g.item, change);
  };

  const handleColumnClick = (event, column) => {
    if (!canEdit || !onCreate || column.key === UNASSIGNED) return;
    if (event.target !== event.currentTarget) return; // clicked a block, not empty space
    const rect = event.currentTarget.getBoundingClientRect();
    const minutes = snap(geom.minutesAtPoint(rect, event));
    const { start, end } = clampToGrid(minutes, 60);
    onCreate({
      columnKey: column.key,
      columnName: column.label,
      // Room columns are per campus, so an empty slot in one identifies where the new
      // lesson belongs even when the grid spans several campuses.
      campusId: column.campusId,
      startTime: toTime(start),
      endTime: toTime(end)
    });
  };

  if (columns.length === 0) {
    return (
      <EmptyState>
        <h3>Nothing scheduled</h3>
        <p>{emptyHint}</p>
      </EmptyState>
    );
  }

  const ghostFor = (columnKey) => {
    if (!drag || drag.columnKey !== columnKey) return null;
    return (
      <DragGhost {...geom.blockStyle(drag.startMin, drag.endMin)} $color={colorOf(drag.item)}>
        {toTime(drag.startMin)}–{toTime(drag.endMin)}
        {drag.dayIndex !== null && drag.dayIndex !== undefined ? ' · moving day' : ''}
      </DragGhost>
    );
  };

  /**
   * One class card. Shared by both orientations — the geometry props are already resolved by
   * the axis descriptor, so this only decides what text fits.
   */
  const renderBlock = (column, item) => {
    const dragging = drag && drag.item.id === item.id && drag.mode === 'move';
    const geomProps = {
      $top: item.$top, $left: item.$left, $width: item.$width, $height: item.$height,
      $zIndex: item.$zIndex
    };

    if (isBusy(item)) {
      return (
        <Block
          key={`${column.key}-${item.id}`}
          {...geomProps}
          $busy
          onClick={(event) => event.stopPropagation()}
          title={`${item.title} · ${item.startTime}-${item.endTime}`}
        >
          <strong>{item.title}</strong>
          {item.rows > 1 && <small>{item.startTime}–{item.endTime}</small>}
        </Block>
      );
    }

    return (
      <Block
        key={`${column.key}-${item.id}`}
        {...geomProps}
        $color={colorOf(item)}
        $cancelled={isCancelled(item)}
        $flagged={isNoShow(item)}
        $draggable={canEdit}
        $dragging={Boolean(dragging)}
        onPointerDown={(event) => beginGesture(event, item, column.key, 'move')}
        onClick={(event) => { if (!canEdit) { event.stopPropagation(); onSelect(item); } }}
        title={`${item.title} · ${item.startTime}-${item.endTime}${item.room ? ` · ${item.room}` : ''}`}
      >
        {hasConflict(item) && (
          <ConflictMark title={`Clashes: ${item.conflicts.join(', ')}`}>!</ConflictMark>
        )}
        <strong>
          {item.lessonType !== 'regular' && <Badge>{item.lessonType}</Badge>}
          {item.title || '(untitled)'}
        </strong>
        {item.rows > 1 && <small>{item.startTime}–{item.endTime}</small>}
        {item.rows > 1 && (
          <small>
            {groupBy === 'teacher'
              ? (item.room || 'No room')
              : (item.teachers || []).map(t => t.name).join(', ') || 'No teacher'}
            {/* A teacher column can hold classes from two campuses at once, and
                "Room 3" alone would not say which building to walk to. */}
            {multiCampus && groupBy === 'teacher' && item.campusName
              ? ` · ${item.campusName}`
              : ''}
          </small>
        )}
        {/* Who is in the room, which is the question a card is usually opened to answer.
            Falls back to the headcount when there is nobody named — an empty class, or a
            group whose enrolments have not been entered yet. */}
        {item.rows > 2 && (
          <small>
            {item.year ? `Y${item.year} · ` : ''}
            {formatStudentNames(item)
              || `${formatEnrolment(item)}${item.capacityLimit ? ' students' : ' enrolled'}`}
          </small>
        )}
        {item.rows > 3 && formatStudentNames(item) && (
          <small>
            {formatEnrolment(item)}{item.capacityLimit ? ' students' : ' enrolled'}
          </small>
        )}
        {canEdit && onResize && !isCancelled(item) && (
          <ResizeHandle
            $horizontal={horizontal}
            onPointerDown={(event) => beginGesture(event, item, column.key, 'resize')}
          />
        )}
      </Block>
    );
  };

  const pointerHandlers = {
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerUp
  };

  if (horizontal) {
    return (
      <HGrid {...pointerHandlers}>
        <HInner $trackWidth={axes.trackWidth}>
          <HCorner />
          <HTimeHeader>
            {hours.map((hour, index) => (
              <HTimeTick key={hour} $left={index * HOUR_WIDTH}>{hour}</HTimeTick>
            ))}
          </HTimeHeader>

          {laidOut.map(column => (
            <React.Fragment key={column.key}>
              <HLaneLabel>
                {multiCampus && column.campus && <HLaneCampus>{column.campus}</HLaneCampus>}
                {column.label}
                <span>{column.sublabel}</span>
              </HLaneLabel>
              <HLaneTrack
                ref={setColumnRef(column.key)}
                $creatable={canEdit && Boolean(onCreate) && column.key !== UNASSIGNED}
                $isDropTarget={Boolean(drag) && drag.mode === 'move' && drag.columnKey === column.key}
                onClick={(event) => handleColumnClick(event, column)}
              >
                {hours.map((_, index) => <HHourLine key={index} $left={index * HOUR_WIDTH} />)}
                {column.items.map(item => renderBlock(column, item))}
                {ghostFor(column.key)}
              </HLaneTrack>
            </React.Fragment>
          ))}
        </HInner>
      </HGrid>
    );
  }

  return (
    <Grid $columns={laidOut.length} $hours={trackHours} {...pointerHandlers}>
      <TimeHeaderCell>Time</TimeHeaderCell>
      {laidOut.map(column => (
        <GridHeaderCell key={column.key}>
          {multiCampus && column.campus && <ColumnCampus>{column.campus}</ColumnCampus>}
          {column.label}
          <span>{column.sublabel}</span>
        </GridHeaderCell>
      ))}

      <TimeColumn>
        {hours.map(hour => <TimeCell key={hour}>{hour}</TimeCell>)}
      </TimeColumn>

      {laidOut.map(column => (
        <GridColumn
          key={column.key}
          $hours={trackHours}
          ref={setColumnRef(column.key)}
          $creatable={canEdit && Boolean(onCreate) && column.key !== UNASSIGNED}
          $isDropTarget={Boolean(drag) && drag.mode === 'move' && drag.columnKey === column.key}
          onClick={(event) => handleColumnClick(event, column)}
        >
          {hours.map((_, index) => <HourLine key={index} $top={index * HOUR_HEIGHT} />)}
          {column.items.map(item => renderBlock(column, item))}
          {ghostFor(column.key)}
        </GridColumn>
      ))}
    </Grid>
  );
};

export default GridView;
